/**
 * LinkedResearchSection — appended to the CaseDetail page to show which
 * Industry Research and Company Research nodes back this case, with
 * same-tab deep-links and a freshness indicator.
 *
 * Fetches /case-discovery/case-context/{caseId} independently of the rest of
 * the page. Empty state shows "Run Industry/Company Research →" CTA stubs
 * that link to the existing creation flows.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Briefcase,
  ExternalLink,
  Loader2,
  Link as LinkIcon,
} from 'lucide-react';
import Card, { CardBody, CardHeader } from '../ui/Card';
import Button from '../ui/Button';
import { api } from '../../lib/api';
import IndustryFreshnessBadge from './IndustryFreshnessBadge';

interface LinkedResearchSectionProps {
  caseId: string | undefined;
}

interface CaseContextResponse {
  caseId: string;
  company?: {
    companyId?: string | null;
    name?: string | null;
    country?: string | null;
    sector?: string | null;
  } | null;
  industry?: {
    industryId?: string | null;
    name?: string | null;
    versionString?: string | null;
    lastUpdated?: string | null;
  } | null;
  linkedResearch?: {
    industryDetailUrl?: string | null;
    companyDetailUrl?: string | null;
    industryFreshness?: {
      researchedAt?: string | null;
      ageInDays?: number | null;
      versionString?: string | null;
    } | null;
  } | null;
}

const LinkedResearchSection: React.FC<LinkedResearchSectionProps> = ({ caseId }) => {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery<CaseContextResponse>({
    queryKey: ['case-context', 'envelope', caseId],
    queryFn: () => api.get(`/case-discovery/case-context/${caseId}`),
    enabled: !!caseId,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  if (!caseId) return null;

  if (isLoading) {
    return (
      <Card>
        <CardBody>
          <div className="flex items-center gap-2 text-neutral-600">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Loading linked research…</span>
          </div>
        </CardBody>
      </Card>
    );
  }

  const industry = data?.industry;
  const company = data?.company;
  const freshness = data?.linkedResearch?.industryFreshness;
  const industryUrl = data?.linkedResearch?.industryDetailUrl;
  const companyUrl = data?.linkedResearch?.companyDetailUrl;

  return (
    <Card>
      <CardHeader>
        <h2 className="text-xl font-semibold text-neutral-900 flex items-center gap-2">
          <LinkIcon className="w-5 h-5 text-primary-600" />
          Linked Research
        </h2>
        <p className="text-sm text-neutral-600 mt-1">
          The Industry and Company Research records that back this case. Open them
          to drill into source data or refresh the underlying research.
        </p>
      </CardHeader>
      <CardBody>
        <div className="grid md:grid-cols-2 gap-4">
          {/* Industry Research card */}
          <div className="border border-neutral-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Briefcase className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-neutral-500 mb-0.5">Industry Research</p>
                {industry?.industryId ? (
                  <>
                    <p className="font-semibold text-neutral-900 truncate">
                      {industry.name ?? industry.industryId}
                    </p>
                    <div className="mt-2">
                      <IndustryFreshnessBadge
                        ageInDays={freshness?.ageInDays}
                        versionString={freshness?.versionString ?? industry.versionString}
                      />
                    </div>
                    <button
                      onClick={() => navigate(industryUrl || `/industries/${industry.industryId}`)}
                      className="mt-3 text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      Open Industry Research
                      <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <div>
                    <p className="text-sm text-neutral-500 mb-2">
                      No Industry Research linked yet.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/industries')}
                      className="flex items-center gap-1"
                    >
                      Run Industry Research
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Company Research card */}
          <div className="border border-neutral-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-neutral-500 mb-0.5">Company Research</p>
                {company?.companyId ? (
                  <>
                    <p className="font-semibold text-neutral-900 truncate">
                      {company.name ?? company.companyId}
                    </p>
                    {(company.country || company.sector) && (
                      <p className="text-xs text-neutral-500 mt-1">
                        {[company.sector, company.country].filter(Boolean).join(' • ')}
                      </p>
                    )}
                    <button
                      onClick={() => navigate(companyUrl || `/companies/${company.companyId}`)}
                      className="mt-3 text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      Open Company Research
                      <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <div>
                    <p className="text-sm text-neutral-500 mb-2">
                      No Company Research linked yet.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/companies')}
                      className="flex items-center gap-1"
                    >
                      Run Company Research
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default LinkedResearchSection;
